import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { isBlank, notBlank } from "../../../../util/ext";
import { equals, mergeRight, toLower } from "ramda";
import { useIsAdmin } from "../../../../auth/authUtils";
import {
  useModuleCreate,
  useSuggestInternalCode,
  useUploadModuleThumbnail,
} from "../../../modules/moduleServices";
import {
  useAnthologyCreate,
  useUploadAnthologyThumbnail,
  useUploadAnthologyOrganizationLogo,
} from "../../../anthologies/anthologyServices";
import {
  useCollectionCreate,
  useUploadCollectionThumbnail,
} from "../../../collections/collectionServices";
import {
  validateAnthology,
  validateAnthologyField,
  hasError as hasAnthologyError,
  firstError as firstAnthologyError,
} from "../../../anthologies/anthologyValidation";
import {
  validateCollection,
  validateCollectionField,
  hasError as hasCollectionError,
  firstError as firstCollectionError,
} from "../../../collections/collectionValidation";
import {
  validateNewModule,
  validateNewModuleField,
  hasError as hasModuleError,
  firstError as firstModuleError,
} from "../../../modules/moduleValidation";
import { MediaContext } from "../../../common/media-context";

import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import Divider from "@material-ui/core/Divider";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import MediaDialog from "../../../common/MediaDialog";
import NewContentGroupForm from "./NewContentGroupForm";
import OrganizeButton from "../../common/OrganizeButton";
import SaveButton from "../../../../components/SaveButton";

const useStyles = makeStyles(theme => ({
  container: {
    gap: 10,
  },

  dialogContent: {
    paddingTop: 0,
  },

  edit: {
    padding: 0,
  },

  formControl: {
    width: 250,
    paddingBottom: theme.spacing(6),
  },

  title: {
    color: theme.palette.primary.darker,
  },
}));

const initial = {
  module: {
    name: "",
    mediaType: "module",
    thumbnailUrl: "",
    status: "INACTIVE",
  },

  anthology: {
    name: "",
    subName: "",
    mediaType: "anthology",
    thumbnailUrl: "",
    organizationLogoUrl: "",
    description: "",
    descriptionText: "",
    bisacCategories: [],
  },

  collection: {
    name: "",
    mediaType: "collection",
    thumbnailUrl: "",
    description: "",
    descriptionText: "",
    displayOrder: 1,
    defaultContentSort: "",
  },
};

const validators = {
  module: {
    validateField: validateNewModuleField,
    validate: validateNewModule,
    hasError: hasModuleError,
    firstError: firstModuleError,
  },

  anthology: {
    validateField: validateAnthologyField,
    validate: validateAnthology,
    hasError: hasAnthologyError,
    firstError: firstAnthologyError,
  },

  collection: {
    validateField: validateCollectionField,
    validate: validateCollection,
    hasError: hasCollectionError,
    firstError: firstCollectionError,
  },
};

const thumbnailValidation = {
  thumbnail: ["A thumbnail must be selected"],
};

const NewContentGroupDialog = props => {
  const {
    addToParent,
    handleClose,
    open,
    parent,
    parentPrevMediaListCount,
  } = props;
  const classes = useStyles();
  const isAdmin = useIsAdmin();

  //-- Set up overall state
  const [contentGroup, setContentGroup] = useState({});
  const [orgId, setOrgId] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [orgLogoFile, setOrgLogoFile] = useState(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validator, setValidator] = useState({});
  const [errors, setErrors] = useState({});
  const [mediaType, setMediaType] = useState("");
  const [menuItems, setMenuItems] = useState([
    // "Module",
    "Anthology",
    "Collection",
  ]);

  useEffect(() => {
    if (notBlank(parent)) {
      if (equals(parent.mediaType, "module")) {
        setMenuItems(["Anthology", "Collection"]);
      }

      if (equals(parent.mediaType, "anthology")) {
        setMenuItems(["Collection"]);
      }
    }
  }, [parent]);

  const handleChange = event => {
    const mediaType = event.target.value;

    setMediaType(mediaType);
    setContentGroup(initial[mediaType]);
    setValidator(validators[mediaType]);
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setOrgLogoFile(null);
    setOrgLogoUrl(null);
    setOrgId(null);
    setErrors({});
  };

  //-- Create Content Group Handlers --//
  //-- Modules
  const createModule = useModuleCreate();
  const uploadModuleThumbnail = useUploadModuleThumbnail();
  const createInternalCode = useSuggestInternalCode();

  //-- Anthologies
  const createAnthology = useAnthologyCreate();
  const uploadAnthologyThumbnail = useUploadAnthologyThumbnail();
  const uploadOrgLogo = useUploadAnthologyOrganizationLogo();

  //-- Collections
  const createCollection = useCollectionCreate();
  const uploadCollectionThumbnail = useUploadCollectionThumbnail();

  const handlers = {
    module: {
      create: createModule,
      uploadThumbnail: uploadModuleThumbnail,
    },

    anthology: {
      create: createAnthology,
      uploadThumbnail: uploadAnthologyThumbnail,
    },

    collection: {
      create: createCollection,
      uploadThumbnail: uploadCollectionThumbnail,
    },
  };

  // TODO: pass addToParent handler from ContentGroupsPanel
  const handleClickClose = useCallback(() => {
    handleClose();
    setMediaType("");
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setErrors({});
  }, [handleClose]);

  const handleSave = useCallback(async () => {
    setLoading(true);

    const validation = mergeRight(validator.validate(contentGroup), errors);
    try {
      if (notBlank(validation) || isBlank(thumbnailFile)) {
        if (isBlank(thumbnailFile)) {
          setErrors(mergeRight(validation, thumbnailValidation));
        } else {
          setErrors(validation);
        }

        throw new Error("Please address missing or invalid values");
      }

      if (equals(mediaType, "module")) {
        // Get suggested internal code
        const suggestedInternalCode = await createInternalCode.mutateAsync({
          data: { internalCode: contentGroup.name },
        });
        contentGroup.internalCode = suggestedInternalCode.internalCode;
        contentGroup.displayName = contentGroup.name;
      }

      if (equals(mediaType, "collection") && notBlank(parent)) {
        if (equals(parent.mediaType, "anthology")) {
          // Add collection to end of anthology collection list
          contentGroup.displayOrder = parentPrevMediaListCount + 1;
        }
      }

      // Create new content group
      const newContentGroup = await handlers[mediaType].create.mutateAsync({
        data: contentGroup,
        resourceIds: equals(mediaType, "module")
          ? { orgId: isAdmin ? orgId : null }
          : {},
      });

      await handlers[mediaType].uploadThumbnail.mutateAsync({
        resourceIds: { id: newContentGroup.id },
        data: thumbnailFile,
      });

      // Upload Anthology Org Logo
      if (equals(mediaType, "anthology") && notBlank(orgLogoFile)) {
        await uploadOrgLogo.mutateAsync({
          resourceIds: { id: newContentGroup.id },
          data: orgLogoFile,
        });
      }

      if (notBlank(parent)) {
        await addToParent.mutateAsync({
          resourceIds: {
            id: parent.id,
          },
          data: {
            ids: [newContentGroup.id],
          },
        });
      }

      handleClickClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [
    addToParent,
    contentGroup,
    createInternalCode,
    errors,
    handleClickClose,
    handlers,
    isAdmin,
    mediaType,
    orgId,
    orgLogoFile,
    parent,
    parentPrevMediaListCount,
    thumbnailFile,
    uploadOrgLogo,
    validator,
  ]);

  return (
    <MediaContext.Provider
      value={{
        initial: initial[mediaType],
        media: contentGroup,
        setMedia: setContentGroup,
        errors,
        setErrors,
        thumbnailUrl,
        setThumbnailUrl,
        thumbnailFile,
        setThumbnailFile,
        orgLogoUrl,
        setOrgLogoUrl,
        orgLogoFile,
        setOrgLogoFile,
        orgId,
        setOrgId,
      }}
    >
      <MediaDialog
        open={open}
        fullWidth
        maxWidth="md"
        title={`${
          parent
            ? `Add Content Group to ${parent.name}`
            : "Create New Content Group"
        }`}
        titleClassName={classes.title}
        contentClassName={classes.dialogContent}
        handleClose={handleClickClose}
        content={
          <Grid className={classes.container} container direction="column">
            <Divider />

            <FormControl className={classes.formControl}>
              <InputLabel id="select-content-group-label">
                Content Group
              </InputLabel>
              <Select
                labelId="select-content-group-label"
                id="select-content-group"
                value={mediaType}
                onChange={handleChange}
                MenuProps={{
                  transformOrigin: {
                    horizontal: "left",
                    vertical: "top",
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
              >
                {menuItems.map(item => (
                  <MenuItem key={item} value={toLower(item)}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {notBlank(mediaType) && (
              <NewContentGroupForm
                mediaType={mediaType}
                validator={validator}
              />
            )}
          </Grid>
        }
        actions={
          <Fragment>
            <OrganizeButton color="primary" onClick={handleClickClose}>
              Cancel
            </OrganizeButton>
            <SaveButton
              loading={loading}
              disabled={isBlank(mediaType) || loading}
              onClick={handleSave}
            />
          </Fragment>
        }
      />
    </MediaContext.Provider>
  );
};

export default NewContentGroupDialog;
